import { SelectChangeEvent } from '@mui/material';
import { IField } from '../../../types/my-types';

export type { SelectChangeEvent };

// Type definitions for data type options
// These are the known valid values - used for documentation and type hints
export type ThousandsGrouping = '3, 3' | '2, 3';
export type DecimalSymbol = ',' | '.';
export type DateOrder = 'MDY' | 'DMY' | 'YMD' | 'M[S]Y' | 'M[E]Y' | 'YM[S]' | 'YM[E]';

// objects
// DataTypeOption is for individual field config (single values)
export interface DataTypeOption {
  decimalSymbol?: string;
  inherited?: boolean;
  order?: string;
  thousandsGrouping?: string;
}

// DataTypeAPIOptions is for API response (arrays of available options)
export interface DataTypeAPIOptions {
  decimalSymbol?: string[];
  order?: string[];
  thousandsGrouping?: string[];
}

export interface TrainingSetDataConfig {
  date?: DataTypeOption;
  amount?: DataTypeOption;
  number?: DataTypeOption;
  using_global_data_type_config?: boolean;
}
export interface DataTypesAPIResponse {
  name: string;
  options: DataTypeAPIOptions;
  order?: string[];
}

// props
export interface DataTypeGlobalSelectorProps {
  dataTypesResponse: DataTypesAPIResponse[];
  setClearFieldsFormats: Function;
  trainingSetDataTypeConfig: TrainingSetDataConfig | null | undefined;
  trainingSetId: string;
}
export interface FieldsTopLabelProps {
  trainingSetDataTypeIsGlobal: boolean;
}
export interface FieldsSettingsProps {
  clearFieldsFormats: boolean;
  dataTypesPadding: string;
  dataTypesResponse: DataTypesAPIResponse[];
  field: IField;
  setClearFieldsFormats: Function;
  trainingSetDataTypeConfig?: TrainingSetDataConfig | null;
}
export interface DataTypeSelectorProps {
  dataTypesResponse: DataTypesAPIResponse[];
  dropdownChange: (event: SelectChangeEvent<unknown>) => void;
  fieldDataType: DataTypeOption;
}
export interface FormatSelectorProps {
  dataTypesResponse: DataTypesAPIResponse[];
  dropdownChange: (event: SelectChangeEvent<unknown>) => void | undefined;
  fieldDataType: string;
  fieldDataTypeConfig: DataTypeOption;
  fieldLabel: string;
  trainingSetDataTypeConfig: TrainingSetDataConfig | null | undefined;
}
export interface FieldsSettingsIconsProps {
  fieldDataTypeIsGlobal: boolean;
}

// Constants
export const FIELDS_UI_LABELS: { [key: string]: string } = {
  MDY: 'Month Day Year',
  DMY: 'Day Month Year',
  YMD: 'Year Month Day',
  'M[S]Y': 'Month [Start] Year',
  'M[E]Y': 'Month [End] Year',
  'YM[S]': 'Year Month [Start]',
  'YM[E]': 'Year Month [End]',
  // Composite format labels (decimalSymbol|thousandsGrouping)
  '.|3, 3': '1,000,000.00',
  ',|3, 3': '1.000.000,00',
  '.|2, 3': '1,00,000.00',
  // Legacy single-value labels (for backwards compatibility)
  ',': '1.000,00',
  '.': '1,000.00',
};
export const FIELD_DATA_TYPE_CONFIG_DEFAULT: { [key: string]: string } = {
  date: 'MDY',
  amount: '.',
  number: '.',
};
export const FIELD_THOUSANDS_GROUPING_DEFAULT: ThousandsGrouping = '3, 3';

// Helper to create composite format key
export const getCompositeFormatKey = (
  decimalSymbol?: string,
  thousandsGrouping?: string
): string => {
  const decimal = decimalSymbol || '.';
  const grouping = thousandsGrouping || FIELD_THOUSANDS_GROUPING_DEFAULT;
  return `${decimal}|${grouping}`;
};

// Helper to parse composite format key
export const parseCompositeFormatKey = (
  compositeKey: string
): { decimalSymbol: string; thousandsGrouping: string } => {
  const [decimalSymbol, thousandsGrouping] = compositeKey.split('|');
  return {
    decimalSymbol: decimalSymbol || '.',
    thousandsGrouping: thousandsGrouping || FIELD_THOUSANDS_GROUPING_DEFAULT,
  };
};
